import { Button, Badge, Input, Textarea } from "@devhop/ui";
import { formatDateLocal } from "@/shared/lib/date-time";
import { CheckCircle2, Plus } from "lucide-react";
import { useState } from "react";

type Version = {
  id: string;
  version: string;
  changelog: string;
  isCurrent: boolean;
  publishedAt: string | null;
};

type FormVersioningPanelProps = {
  versions: Version[];
  onAddVersion: (version: string, changelog: string) => void;
};

export function FormVersioningPanel({
  versions,
  onAddVersion,
}: FormVersioningPanelProps) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newVersion, setNewVersion] = useState("");
  const [changelog, setChangelog] = useState("");

  const handleAddVersion = () => {
    if (newVersion.trim()) {
      onAddVersion(newVersion, changelog);
      setNewVersion("");
      setChangelog("");
      setShowAddForm(false);
    }
  };

  const sortedVersions = [...versions].sort((a, b) => {
    // Sort by version number (simple comparison)
    const aParts = a.version.split(".").map(Number);
    const bParts = b.version.split(".").map(Number);
    for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
      const aVal = aParts[i] || 0;
      const bVal = bParts[i] || 0;
      if (aVal !== bVal) return bVal - aVal; // Descending order
    }
    return 0;
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">ການຈັດການເວີຊັນ</h3>
        {!showAddForm && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAddForm(true)}
          >
            <Plus className="mr-2 h-4 w-4" />
            ເພີ່ມເວີຊັນ
          </Button>
        )}
      </div>

      {showAddForm && (
        <div className="rounded-lg border bg-muted/50 p-4">
          <div className="space-y-3">
            <div>
              <label htmlFor="new-version" className="text-sm font-medium">
                ເລກເວີຊັນ
              </label>
              <Input
                id="new-version"
                value={newVersion}
                onChange={(e) => setNewVersion(e.target.value)}
                placeholder="ຕົວຢ່າງ: 1.1, 2.0"
                className="mt-1"
              />
            </div>
            <div>
              <label htmlFor="changelog" className="text-sm font-medium">
                ບັນທຶກການປ່ຽນແປງ
              </label>
              <Textarea
                id="changelog"
                value={changelog}
                onChange={(e) => setChangelog(e.target.value)}
                placeholder="ອະທິບາຍການປ່ຽນແປງໃນເວີຊັນນີ້..."
                className="mt-1"
                rows={3}
              />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAddVersion}>
                ບັນທຶກ
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowAddForm(false);
                  setNewVersion("");
                  setChangelog("");
                }}
              >
                ຍົກເລີກ
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {sortedVersions.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground">
            ຍັງບໍ່ມີເວີຊັນ
          </p>
        ) : (
          sortedVersions.map((version) => (
            <div
              key={version.id}
              className="flex items-start justify-between rounded-lg border bg-card p-3"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">v{version.version}</span>
                  {version.isCurrent && (
                    <Badge variant="default" className="text-xs">
                      <CheckCircle2 className="mr-1 h-3 w-3" />
                      ປັດຈຸບັນ
                    </Badge>
                  )}
                  {version.publishedAt && (
                    <span className="text-muted-foreground text-xs">
                      ເຜີດແພ່: {formatDateLocal(version.publishedAt)}
                    </span>
                  )}
                </div>
                {version.changelog && (
                  <p className="mt-1 text-sm text-muted-foreground">
                    {version.changelog}
                  </p>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
